/**
 * ITER305 · Premium Guest Wallet — Full-Screen Swipe
 *
 * Replaces the ITER303 split-view (thumbnail rail + selected card) with
 * a full-screen, single-voucher horizontal swipe carousel powered by
 * native CSS `scroll-snap-type: x mandatory`. NO third-party sliders,
 * NO custom pan gesture handling.
 *
 * Anatomy (top → bottom):
 *   1. Horizontal filter chips  (All · Dining · Discount · Special · Gift)
 *   2. Snap-scroll rail         (each child is 100 % of viewport, snaps)
 *   3. Dot indicators           (position + tap-to-jump)
 *   4. Action row               (Reveal / Share / Gift buttons under card)
 *   5. "Have a voucher code?"   (redemption code entry strip)
 *
 * Share button:
 *   • Disabled until the voucher's code has been REVEALED.
 *   • On share, the payload includes the FULL unmasked code + name +
 *     validity + outlet — never a masked/preview code.
 */
import React, {
  useMemo, useState, useCallback, useEffect, useRef,
} from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Share2, QrCode, ArrowRight, Sparkles, Gift, Download,
} from 'lucide-react';
import * as htmlToImage from 'html-to-image';
import PremiumVoucherCard from './VoucherCard';
import { QRCodeSVG } from 'qrcode.react';
import VoucherDesignRenderer from './VoucherDesignRenderer';
// Phase 1 · shared presentation gateway (single decision point for
// read-mode rendering across Guest Wallet, thumbnail, and Builder
// preview). Historical WalletCardRender kept below as a thin adapter
// so existing imports do not need to change.
import VoucherPresentation from './VoucherPresentation';

/**
 * Wallet card render router — routes V2 designs to the canonical
 * <VoucherDesignRenderer> (read-only, no editor code paths) and falls
 * through to the legacy <PremiumVoucherCard> for older vouchers.
 * This is the ONLY place the guest wallet decides which renderer to
 * mount. The editor and its libraries never ship with the wallet.
 *
 * Recovery-Phase-0 invariant:
 *   Commercial voucher visibility is NEVER conditional on the design
 *   payload. Any renderer error, missing design, missing template,
 *   archived template or malformed layer is isolated to that one card
 *   via <WalletCardErrorBoundary> and falls back to PremiumVoucherCard
 *   with the same commercial content.
 */
function WalletCardRender(props) {
  // Phase 1 · thin adapter → shared VoucherPresentation gateway.
  return <VoucherPresentation {...props} />;
}

// ── Phase 2B · Premium redemption panel ───────────────────────
// Appears BELOW the voucher after Reveal. Shows the server-issued
// short-lived QR + operational plaintext code + a live countdown to
// the QR expiry. Tapping Refresh reissues a fresh credential without
// changing voucher status or creating a redemption event.
function RedemptionPanel({ reveal, voucher, onRefresh, refreshing }) {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const expIso  = reveal?.qr_expires_at;
  const expMs   = expIso ? new Date(expIso).getTime() : 0;
  const secLeft = Math.max(0, Math.floor((expMs - now) / 1000));
  const mm = String(Math.floor(secLeft / 60)).padStart(2, '0');
  const ss = String(secLeft % 60).padStart(2, '0');
  const expired = secLeft === 0 && !!expMs;
  const qr = expired ? '' : (reveal?.qr_payload || '');
  return (
    <div
      data-testid="wallet-reveal-panel"
      className="mt-4 mx-auto max-w-[360px] rounded-2xl p-4"
      style={{
        background:
          'linear-gradient(135deg, rgba(11,42,30,0.92) 0%, rgba(14,55,40,0.92) 100%)',
        border: '1px solid rgba(212,175,55,0.30)',
        boxShadow:
          '0 24px 60px -18px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(255,255,255,0.05)',
        color: '#F7F5EE',
      }}
    >
      <div
        className="text-[10px] uppercase tracking-[0.28em] text-center mb-2"
        style={{ color: '#D4AF37', fontFamily: 'Cormorant Garamond, serif' }}
      >
        Ready to Redeem
      </div>
      <div className="flex items-center justify-center mb-3">
        <div
          className="rounded-xl p-3"
          style={{ background: '#F7F5EE', width: 168, height: 168 }}
        >
          {qr ? (
            <QRCodeSVG value={qr} size={144}
              fgColor="#0B2A1E" bgColor="#F7F5EE"
              level="M" includeMargin={false}
              style={{ width: '100%', height: '100%' }}
            />
          ) : (
            <div
              className="w-full h-full flex flex-col items-center justify-center"
              style={{ color: 'rgba(11,42,30,0.60)',
                       fontFamily: 'Inter', fontSize: 11,
                       letterSpacing: '0.15em', textTransform: 'uppercase' }}
            >
              QR expired<br/>Tap Refresh
            </div>
          )}
        </div>
      </div>
      <div className="text-center">
        <div
          className="text-[10px] uppercase tracking-[0.20em]"
          style={{ color: 'rgba(247,245,238,0.55)' }}
        >
          Voucher Code
        </div>
        <div
          className="font-mono text-[15px] tracking-[0.22em]"
          data-testid="wallet-reveal-code"
          style={{ color: '#F7F5EE' }}
        >
          {reveal?.code || '—'}
        </div>
      </div>
      <div
        className="text-[10px] text-center mt-2"
        style={{ color: expired ? '#F87171' : 'rgba(212,175,55,0.85)',
                 fontFamily: 'Inter', letterSpacing: '0.06em' }}
      >
        {expired
          ? 'QR expired. Refresh below.'
          : `Valid for presentation ${mm}:${ss}`}
      </div>
      <button
        onClick={onRefresh}
        disabled={refreshing}
        data-testid="wallet-reveal-refresh"
        className="mt-3 w-full h-9 rounded-lg text-[12px] font-semibold
                   inline-flex items-center justify-center gap-1.5 disabled:opacity-40"
        style={{ background: 'rgba(212,175,55,0.15)',
                 border: '1px solid rgba(212,175,55,0.45)',
                 color: '#D4AF37' }}
      >
        {refreshing ? 'Refreshing…' : (expired ? 'Refresh QR' : 'Reveal Again')}
      </button>
      <div
        className="mt-2 text-[10px] text-center"
        style={{ color: 'rgba(255,255,255,0.45)' }}
      >
        Show this screen to our team.
      </div>
    </div>
  );
}

import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

function categoryFor(v) {
  const d = v.design || {};
  if (d.icon === 'dining' || d.icon === 'drinks') return 'dining';
  if (d.icon === 'gift')                          return 'gift';
  if (d.icon === 'celebration' || d.icon === 'crown') return 'special';
  const tpl = (d.template_id || '').toLowerCase();
  if (tpl.includes('dining'))    return 'dining';
  if (tpl.includes('gift'))      return 'gift';
  if (tpl.includes('birthday') || tpl.includes('anniversary')) return 'special';
  const bt = (v.benefit_type || '').toLowerCase();
  if (bt === 'complimentary_item' || bt === 'buy_one_get_one') return 'dining';
  if (bt === 'flat_discount' || bt === 'percent_discount' || bt === 'cashback')
    return 'discount';
  return 'other';
}

function stateFor(v) {
  const s = v.status;
  if (s === 'redeemed') return 'used';
  if (s === 'expired')  return 'expired';
  if (s === 'paused')   return 'paused';
  return 'available';
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-IN',
      { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return ''; }
}

const CATEGORY_CHIPS = [
  { key: 'all',      label: 'All' },
  { key: 'dining',   label: 'Dining' },
  { key: 'discount', label: 'Discount' },
  { key: 'special',  label: 'Special' },
  { key: 'gift',     label: 'Gift' },
];

export default function GuestVoucherWallet({
  vouchers = [], outlet, config, sessionToken, onRefresh,
  memberStatus = 'active',
}) {
  const [filter, setFilter] = useState('all');
  const [activeIdx, setActiveIdx] = useState(0);
  const [revealedByV, setRevealedByV] = useState({});
  const [revealingId, setRevealingId] = useState(null);
  const [codeInput, setCodeInput] = useState('');
  const [giftOpen, setGiftOpen] = useState(false);
  const [giftRecipient, setGiftRecipient] = useState('');
  const [giftMessage, setGiftMessage] = useState('');
  const [giftSender, setGiftSender] = useState('');
  const [giftCreated, setGiftCreated] = useState(null);
  const [giftBusy, setGiftBusy] = useState(false);
  const giftCardRef = useRef(null);
  const railRef = useRef(null);

  const outletName = outlet?.name || config?.program_name || '';
  const outletLogo = config?.logo_url || null;
  const outletInitials = useMemo(() => (
    (outletName || 'RP').split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase()
  ), [outletName]);

  const enriched = useMemo(() => (
    (vouchers || []).map((v) => ({
      ...v,
      _category: categoryFor(v),
      _state:    stateFor(v),
      _display: {
        name:          v.voucher_type_name || v.name || 'Voucher',
        subtitle:      v.description || v.subtitle || '',
        benefit_type:  v.benefit_type,
        benefit_value: v.benefit_value,
        code:          v.voucher_code || v.code || '',
        design:        v.design || null,
      },
    }))
  ), [vouchers]);

  const counts = useMemo(() => {
    const c = { all: enriched.length, dining: 0, discount: 0, special: 0, gift: 0, other: 0 };
    enriched.forEach((v) => { c[v._category] = (c[v._category] || 0) + 1; });
    return c;
  }, [enriched]);

  const filtered = useMemo(() => {
    if (filter === 'all') return enriched;
    return enriched.filter((v) => v._category === filter);
  }, [enriched, filter]);

  // Whenever the filter list changes, snap to card 0 so the guest
  // never lands on an out-of-range card.
  useEffect(() => {
    setActiveIdx(0);
    // Reset scroll position after paint so scroll-snap doesn't leave
    // us stranded mid-card.
    const el = railRef.current;
    if (el) el.scrollTo({ left: 0, behavior: 'auto' });
  }, [filter]);

  // Track scroll position → active dot indicator. Uses `scroll` +
  // `getBoundingClientRect` because scroll-snap doesn't emit events.
  useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    const onScroll = () => {
      const w = el.clientWidth || 1;
      const idx = Math.round(el.scrollLeft / w);
      setActiveIdx((prev) => (prev === idx ? prev : idx));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [filtered.length]);

  const selected = filtered[activeIdx] || null;

  const jumpTo = (idx) => {
    const el = railRef.current;
    if (!el) return;
    el.scrollTo({ left: idx * el.clientWidth, behavior: 'smooth' });
  };

  const reveal = useCallback(async (v) => {
    if (!v || v._state !== 'available') return;
    setRevealingId(v.id);
    try {
      const res = await axios.post(
        `${API}/api/loyalty/guest/wallet/reveal-voucher`,
        { voucher_instance_id: v.id },
        { headers: { 'X-Loyalty-Session': sessionToken } },
      );
      // Phase 2B · store the full reveal presentation (plaintext code
      // for manual fallback + signed short-lived QR payload + expiry).
      // Reveal state is stored per voucher_instance_id so carousel
      // movement between vouchers cannot leak credentials sideways.
      setRevealedByV((m) => ({ ...m, [v.id]: {
        code:            res.data.voucher_code,
        qr_payload:      res.data.qr_payload,
        qr_expires_at:   res.data.qr_expires_at,
        qr_ttl_seconds:  res.data.qr_ttl_seconds,
        revealed_at:     res.data.revealed_at,
      }}));
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reveal failed');
    } finally { setRevealingId(null); }
  }, [sessionToken, onRefresh]);

  // Phase 2B · Refresh QR — reissue a fresh short-lived credential
  // without changing voucher status or creating a redemption event.
  const refreshQr = useCallback((v) => reveal(v), [reveal]);

  // ── Phase 2B · Share (requires reveal, NEVER shares QR token) ──
  const share = async (v) => {
    if (!v) return;
    const r = revealedByV[v.id];
    if (!r) {
      toast.info('Reveal the code before sharing.');
      return;
    }
    const valid = formatDate(v.valid_until);
    const lines = [
      v._display.name,
      `Code: ${r.code}`,               // plaintext operational code only
      valid ? `Valid until: ${valid}` : null,
      outletName ? `Redeem at: ${outletName}` : null,
    ].filter(Boolean);
    // NOTE · we deliberately do NOT include r.qr_payload — the QR
    // credential is short-lived and would be useless to the recipient
    // by the time they open the share message.
    const text = lines.join('\n');
    try {
      if (navigator.share) {
        await navigator.share({ title: v._display.name, text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success('Voucher details copied to clipboard');
      }
    } catch { /* user cancelled */ }
  };

  const enterCode = async () => {
    if (!codeInput.trim()) return;
    try {
      await navigator.clipboard?.writeText(codeInput.trim());
      toast.success('Show this code at the counter to redeem.', { duration: 4000 });
      setCodeInput('');
    } catch {
      toast.info('Enter the code at the counter to redeem.');
    }
  };

  const openGiftFlow = () => {
    setGiftRecipient(''); setGiftMessage(''); setGiftSender('');
    setGiftCreated(null); setGiftOpen(true);
  };
  const createGift = async () => {
    if (!selected) return;
    if (!giftRecipient.trim()) { toast.error('Recipient name is required'); return; }
    setGiftBusy(true);
    try {
      const res = await axios.post(`${API}/api/loyalty/gift/create`, {
        voucher_type_id: selected.voucher_type_id,
        recipient_name:  giftRecipient.trim(),
        message:         giftMessage.trim() || null,
        sender_name:     giftSender.trim() || null,
      }, {
        // ITER306 · SEC-002 — gift creation is now authenticated.
        // Pass the guest's session token so the backend knows who is
        // minting the voucher (and can rate-limit them).
        headers: { 'X-Loyalty-Session': sessionToken },
      });
      setGiftCreated(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create gift');
    } finally { setGiftBusy(false); }
  };

  const downloadGiftPng = async () => {
    if (!giftCardRef.current) return;
    try {
      const url = await htmlToImage.toPng(giftCardRef.current, {
        pixelRatio: 2, cacheBust: true, skipFonts: true,
      });
      const a = document.createElement('a');
      a.download = `gift-voucher-${giftCreated?.voucher_code || 'card'}.png`;
      a.href = url; a.click();
      toast.success('Card image downloaded');
    } catch {
      toast.error('Could not generate image — try screenshotting the card.');
    }
  };

  const shareGiftLink = async () => {
    if (!giftCreated) return;
    // ITER310 · Prefer the short link when the backend returned one.
    // Falls back to the long token URL in preview / older responses.
    const link = giftCreated.short_url
      ? (giftCreated.short_url.startsWith('http')
           ? giftCreated.short_url
           : `${window.location.origin}${giftCreated.short_url}`)
      : `${window.location.origin}${giftCreated.public_url}`;
    // Premium copy per ITER310 spec. Every field falls back cleanly so
    // half-completed forms still produce a readable message.
    const who = giftRecipient?.trim() || 'there';
    const from = giftCreated.sender_name?.trim() || 'A friend';
    const outlet = giftCreated.outlet_name || '';
    const voucher = giftCreated.voucher_name || 'a special voucher';
    const text = `Hi ${who}! 🎁 ${from} has sent you a gift${
      outlet ? ` from ${outlet}` : ''
    } — ${voucher}. Tap to open your voucher: ${link}`;
    try {
      if (navigator.share) {
        // Duplication fix: put the link INSIDE the text and DO NOT pass
        // `url` — otherwise the OS bakes the URL a second time at the
        // end of the WhatsApp / iMessage preview.
        await navigator.share({ title: 'Gift Voucher', text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success('Gift message copied to clipboard');
      }
    } catch { /* user cancelled */ }
  };

  const selectedReveal = selected ? revealedByV[selected.id] : null;
  const displayedCode = selectedReveal?.code || (selected?._display?.code || '');
  const isRevealed = !!selectedReveal;
  const memberBlocked = memberStatus === 'on_hold' || memberStatus === 'blacklisted';
  const canReveal = selected && selected._state === 'available' && !isRevealed && !memberBlocked;

  return (
    <div className="px-4 sm:px-6 py-4" data-testid="premium-wallet">
      {/* ── Filter chips ────────────────────────────── */}
      <div className="flex overflow-x-auto gap-2 pb-2 -mx-1 px-1"
           data-testid="wallet-filter-chips">
        {CATEGORY_CHIPS.map((c) => {
          const count = counts[c.key] || 0;
          const active = filter === c.key;
          return (
            <button
              key={c.key}
              onClick={() => setFilter(c.key)}
              disabled={c.key !== 'all' && count === 0}
              data-testid={`wallet-chip-${c.key}`}
              className={`flex-shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-full
                          text-[12px] font-medium transition-colors
                          disabled:opacity-30 disabled:cursor-not-allowed ${
                active
                  ? 'text-slate-900'
                  : 'text-white/80 hover:text-white'
              }`}
              style={active
                ? { background: 'var(--gold)', color: '#0B2A5B' }
                : { background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.14)' }}
            >
              {c.label}
              <span className={`text-[10px] font-semibold ${active ? 'opacity-80' : 'opacity-60'}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="mt-16 text-center" data-testid="wallet-empty"
             style={{ color: 'rgba(255,255,255,0.65)' }}>
          <Sparkles className="w-6 h-6 mx-auto mb-2 opacity-40" />
          <div className="text-sm font-medium">No vouchers in this category</div>
          <div className="text-xs mt-1 opacity-70">
            Try a different filter or check back soon.
          </div>
        </div>
      ) : (
        <>
          {/* ── Snap-scroll rail ─────────────────────── */}
          <div
            ref={railRef}
            data-testid="wallet-snap-rail"
            className="mt-4 flex overflow-x-auto -mx-4 sm:-mx-6 px-4 sm:px-6 gap-4
                       [scrollbar-width:none] [-ms-overflow-style:none]"
            style={{
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
              scrollPaddingLeft: '1rem',
              scrollPaddingRight: '1rem',
            }}
          >
            {/* Hide native scrollbar in webkit */}
            <style>{`[data-testid="wallet-snap-rail"]::-webkit-scrollbar{display:none}`}</style>
            {filtered.map((v) => {
              // Phase 2A · adaptive wallet container. Derive the aspect
              // ratio from the saved design so a wide voucher does not
              // leave a portrait void below and a tall voucher is not
              // squeezed inside a landscape container.
              const d = _displayFor(v).design || {};
              const savedAr = Number(d?.canvas?.aspect_ratio);
              const walletAr = Number.isFinite(savedAr) && savedAr > 0
                ? savedAr
                : 1.586;
              // Width fixed to the wallet stage cap; height derives.
              const CARD_W = 340;
              const cardH  = Math.round(CARD_W / walletAr);
              return (
              <div
                key={v.id}
                data-testid={`wallet-slide-${v.id}`}
                className="shrink-0 w-full flex justify-center"
                style={{ scrollSnapAlign: 'center', scrollSnapStop: 'always' }}
              >
                <div
                  className="relative rounded-[26px] p-[6px] w-full"
                  style={{
                    maxWidth: CARD_W + 12,
                    height: cardH + 12,        // stage matches actual card
                    background:
                      'linear-gradient(135deg, rgba(201,162,39,0.30) 0%, rgba(201,162,39,0.06) 40%, rgba(201,162,39,0.30) 100%)',
                    boxShadow:
                      '0 24px 60px -18px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.05)',
                  }}
                >
                  <WalletCardRender
                    voucher={_displayFor(v)}
                    design={_displayFor(v).design}
                    outletName={outletName}
                    outletInitials={outletInitials}
                    outletLogo={outletLogo}
                    state={v._state}
                    validUntil={formatDate(v.valid_until)}
                    size="phone"
                    revealed={!!revealedByV[v.id]}
                    qrPayload={revealedByV[v.id]?.qr_payload}
                    revealedCode={revealedByV[v.id]?.code}
                  />
                </div>
              </div>
            );})}
          </div>

          {/* ── Dot indicators ──────────────────────── */}
          {filtered.length > 1 && (
            <div
              className="flex items-center justify-center gap-1.5 mt-3"
              data-testid="wallet-dots"
            >
              {filtered.map((v, i) => (
                <button
                  key={v.id}
                  onClick={() => jumpTo(i)}
                  aria-label={`Show voucher ${i + 1}`}
                  data-testid={`wallet-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === activeIdx ? 'w-6' : 'w-1.5'
                  }`}
                  style={{
                    background: i === activeIdx
                      ? 'var(--gold)'
                      : 'rgba(255,255,255,0.28)',
                  }}
                />
              ))}
            </div>
          )}

          {/* ── Action row ──────────────────────────── */}
          {selected && (() => {
            // F-6 — Share is not rendered at all before Reveal. Grid columns
            // adapt so the remaining buttons stay evenly weighted.
            const actionsCount =
              (isRevealed ? 1 : 0) + (selected.is_giftable ? 1 : 0) + 1;
            const gridClass = actionsCount === 3
              ? 'grid-cols-3'
              : actionsCount === 2 ? 'grid-cols-2' : 'grid-cols-1';
            return (
            <div
              className={`w-full max-w-[380px] mx-auto mt-5 grid gap-2 ${gridClass}`}
              data-testid="wallet-actions"
            >
              {isRevealed && (
                <button
                  onClick={() => share(selected)}
                  data-testid="wallet-share"
                  title="Share this voucher"
                  className="h-11 rounded-lg text-[12px] font-medium
                              inline-flex items-center justify-center gap-1.5
                              transition-opacity"
                  style={{
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.16)',
                    color: '#FFFFFF',
                  }}
                >
                  <Share2 className="w-3.5 h-3.5" />
                  Share
                </button>
              )}

              {selected.is_giftable && (
                <button
                  onClick={openGiftFlow}
                  data-testid="wallet-gift"
                  className="h-11 rounded-lg text-[12px] font-semibold
                              inline-flex items-center justify-center gap-1.5
                              transition-colors"
                  style={{
                    background: 'rgba(201,163,78,0.14)',
                    border: '1px solid var(--gold)',
                    color: 'var(--gold)',
                  }}
                >
                  <Gift className="w-3.5 h-3.5" />
                  Gift
                </button>
              )}

              <button
                onClick={() => reveal(selected)}
                disabled={!canReveal || revealingId === selected.id}
                data-testid="wallet-redeem"
                className="h-11 rounded-lg text-[12px] font-bold
                            inline-flex items-center justify-center gap-1.5
                            transition-colors
                            disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'var(--gold)', color: '#0B2A5B' }}
              >
                <QrCode className="w-3.5 h-3.5" />
                {isRevealed
                  ? 'Revealed'
                  : selected._state === 'paused'
                    ? 'Temporarily Unavailable'
                    : memberBlocked
                      ? 'Unavailable'
                      : revealingId === selected.id ? 'Revealing…' : 'Reveal'}
              </button>
            </div>
            );
          })()}

          {isRevealed && (
            <RedemptionPanel
              reveal={selectedReveal}
              voucher={selected}
              onRefresh={() => refreshQr(selected)}
              refreshing={revealingId === selected.id}
            />
          )}
        </>
      )}

      {/* Manual guest-side redemption block removed in final closure:
          voucher redemption belongs exclusively to Manager / Outlet
          staff. Guests use Reveal to display the signed credential;
          staff scan / enter that credential on the Manager surface. */}

      {/* ── Gift dialog (unchanged from ITER304) ─────── */}
      <Dialog open={giftOpen} onOpenChange={setGiftOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Gift this voucher</DialogTitle>
            <DialogDescription>
              We&apos;ll create a personalised gift card you can share via
              WhatsApp, email or link.
            </DialogDescription>
          </DialogHeader>

          {!giftCreated ? (
            <div className="space-y-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">
                  Recipient name
                </Label>
                <Input value={giftRecipient}
                       onChange={(e) => setGiftRecipient(e.target.value)}
                       placeholder="e.g. Alex Kumar"
                       data-testid="gift-recipient-input" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">
                  Your name (optional)
                </Label>
                <Input value={giftSender}
                       onChange={(e) => setGiftSender(e.target.value)}
                       placeholder="Sender name"
                       data-testid="gift-sender-input" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-[11px] uppercase tracking-wider text-slate-500">
                    Personalised message (optional)
                  </Label>
                  <span className="text-[10px] text-slate-400"
                        data-testid="gift-message-counter">
                    {giftMessage.length}/280
                  </span>
                </div>
                <Textarea value={giftMessage}
                          onChange={(e) => setGiftMessage(e.target.value)}
                          rows={2}
                          maxLength={280}
                          placeholder="Happy birthday! Enjoy a meal on me"
                          data-testid="gift-message-input" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setGiftOpen(false)}>Cancel</Button>
                <Button onClick={createGift}
                        disabled={giftBusy || !giftRecipient.trim()}
                        data-testid="gift-create-btn"
                        style={{ background: 'var(--primary)', color: 'white' }}>
                  {giftBusy ? 'Creating…' : 'Create Gift'}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-slate-900 rounded-xl p-4 flex items-center
                              justify-center" ref={giftCardRef}>
                <WalletCardRender
                  voucher={{
                    name: selected?._display.name,
                    subtitle: giftMessage
                      ? `For ${giftRecipient} · ${giftMessage}`
                      : `For ${giftRecipient}`,
                    benefit_type: selected?._display.benefit_type,
                    benefit_value: selected?._display.benefit_value,
                    code: giftCreated.voucher_code,
                  }}
                  design={selected?._display.design}
                  outletName={outletName}
                  outletLogo={outletLogo}
                  outletInitials={outletInitials}
                  size="phone"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={downloadGiftPng}
                        data-testid="gift-download-png">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Download PNG
                </Button>
                <Button onClick={shareGiftLink}
                        data-testid="gift-share-link"
                        style={{ background: 'var(--primary)', color: 'white' }}>
                  <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share Link
                </Button>
              </div>
              <div className="text-[11px] text-center text-slate-500">
                Gift code: <span className="font-mono">{giftCreated.voucher_code}</span>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setGiftOpen(false)}>Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Extract the memoised _display block off an enriched voucher.
// Kept as a plain helper so the JSX above stays readable.
function _displayFor(v) { return v._display; }
